import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

export interface SectionPickerCandidate {
  path: string;
  urlCount: number;
  recentCount: number;
  sampleTitles: string[];
}

interface Props {
  candidates: SectionPickerCandidate[];
  hostname: string;
  loading?: boolean;
  onSelect: (path: string) => void;
  onBack: () => void;
}

export function SectionPicker({
  candidates,
  hostname,
  loading,
  onSelect,
  onBack,
}: Props) {
  return (
    <div className="space-y-5 animate-in fade-in duration-300">
      <button
        onClick={onBack}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-gray-900 transition-colors disabled:opacity-50"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="space-y-1 text-left">
        <h3 className="font-bold text-gray-900 text-base">
          Which section should we monitor?
        </h3>
        <p className="text-sm text-muted-foreground">
          We found a few areas of content on <span className="font-medium text-gray-700">{hostname}</span>. Pick the one with articles you'd like autopiloted.
        </p>
      </div>

      <div className="grid gap-2.5">
        {candidates.map((c) => (
          <button
            key={c.path}
            onClick={() => onSelect(c.path)}
            disabled={loading}
            className="group relative w-full min-w-0 text-left rounded-xl border-2 border-gray-100 bg-white p-4 hover:border-primary/40 hover:bg-primary/[0.02] transition-colors disabled:opacity-60 disabled:hover:border-gray-100 disabled:hover:bg-white"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 mb-1 min-w-0">
                  <span className="font-mono text-sm font-bold text-gray-900 truncate min-w-0">
                    {c.path}
                  </span>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                    {c.urlCount} article{c.urlCount === 1 ? "" : "s"}
                    {c.recentCount > 0 && c.recentCount !== c.urlCount && (
                      <> · <span className="text-green-600 font-medium">{c.recentCount} recent</span></>
                    )}
                  </span>
                </div>
                {c.sampleTitles.length > 0 && (
                  <p className="text-xs text-muted-foreground italic break-words">
                    e.g. {c.sampleTitles.slice(0, 3).join(", ")}
                  </p>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:text-primary transition-colors" />
            </div>
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking articles in this section...
        </div>
      )}
    </div>
  );
}
